import { useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { FileText, Plus, Search } from 'lucide-react';
import { ROTULO_STATUS, statusEfetivo, type StatusCotacao } from '@profitlens/dominio';
import { Cabecalho } from '../componentes/Cabecalho';
import { Carregando, Erro, Vazio } from '../componentes/Estados';
import { SeloCotacao } from '../componentes/SeloCotacao';
import { DataBR, Texto } from '../componentes/Valor';
import { useAuth } from '../contextos/AuthContext';
import { useFornecedores } from '../servicos/fornecedores';
import { hojeISO, useCotacoes } from '../servicos/cotacoes';

const ORDEM_FILTRO: StatusCotacao[] = [
  'RASCUNHO',
  'AGUARDANDO_APROVACAO',
  'ENVIADA',
  'PARCIALMENTE_RESPONDIDA',
  'RESPONDIDA',
  'NECESSITA_REVISAO',
  'EXPIRADA',
  'CANCELADA',
];

export function Cotacoes() {
  const { cotacoes, carregando, erro } = useCotacoes();
  const { fornecedores } = useFornecedores();
  const { podeEscrever } = useAuth();
  const [parametros, setParametros] = useSearchParams();
  const [busca, setBusca] = useState('');

  const filtroStatus = parametros.get('status') ?? 'TODOS';
  const hoje = hojeISO();

  const nomeFornecedor = useMemo(
    () =>
      Object.fromEntries(
        fornecedores.map((f) => [f.id, f.nomeFantasia || f.razaoSocial]),
      ) as Record<string, string>,
    [fornecedores],
  );

  /**
   * O status usado no filtro e na lista e o EFETIVO, nao o gravado. Uma cotacao
   * cuja validade passou aparece como expirada sem que ninguem tenha escrito
   * isso em lugar nenhum (RN-041).
   */
  const comStatus = useMemo(
    () =>
      cotacoes.map((c) => ({
        ...c,
        statusEfetivo: statusEfetivo(c.status, c.validade, hoje),
      })),
    [cotacoes, hoje],
  );

  const filtradas = useMemo(() => {
    const t = busca.trim().toLowerCase();
    return comStatus.filter((c) => {
      if (filtroStatus !== 'TODOS' && c.statusEfetivo !== filtroStatus) return false;
      if (!t) return true;
      return [c.referencia, nomeFornecedor[c.fornecedorId], c.observacoes]
        .filter(Boolean)
        .join(' ')
        .toLowerCase()
        .includes(t);
    });
  }, [comStatus, busca, filtroStatus, nomeFornecedor]);

  const contagem = useMemo(() => {
    const c: Partial<Record<StatusCotacao, number>> = {};
    for (const cot of comStatus) c[cot.statusEfetivo] = (c[cot.statusEfetivo] ?? 0) + 1;
    return c;
  }, [comStatus]);

  if (carregando) return <Carregando />;
  if (erro) return <Erro titulo="Não foi possível carregar as cotações" detalhe={erro} />;

  function trocarFiltro(valor: string) {
    const proximo = new URLSearchParams(parametros);
    if (valor === 'TODOS') proximo.delete('status');
    else proximo.set('status', valor);
    setParametros(proximo, { replace: true });
  }

  return (
    <div className="flex flex-col gap-5">
      <Cabecalho
        titulo="Cotações"
        descricao="O que cada fornecedor cobra, e desde quando o preço vale."
        acoes={
          podeEscrever && (
            <Link
              to="/cotacoes/nova"
              className="inline-flex items-center gap-1.5 rounded bg-marca px-3 py-1.5 text-sm font-semibold text-marca-tinta hover:opacity-90"
            >
              <Plus className="size-4" aria-hidden />
              Nova cotação
            </Link>
          )
        }
      />

      <div className="flex flex-wrap gap-2">
        <label className="relative min-w-56 flex-1">
          <Search className="absolute top-1/2 left-2.5 size-4 -translate-y-1/2 text-tinta-3" aria-hidden />
          <input
            type="search"
            value={busca}
            onChange={(e) => setBusca(e.target.value)}
            placeholder="Buscar por referência ou fornecedor"
            className="w-full rounded border border-regua-2 bg-superficie py-1.5 pr-3 pl-8 text-sm outline-none focus:border-marca"
          />
        </label>
        <select
          value={filtroStatus}
          onChange={(e) => trocarFiltro(e.target.value)}
          aria-label="Filtrar por status"
          className="rounded border border-regua-2 bg-superficie px-3 py-1.5 text-sm outline-none focus:border-marca"
        >
          <option value="TODOS">Todos os status ({comStatus.length})</option>
          {ORDEM_FILTRO.map((s) => (
            <option key={s} value={s}>
              {ROTULO_STATUS[s]} ({contagem[s] ?? 0})
            </option>
          ))}
        </select>
      </div>

      {filtradas.length === 0 ? (
        <Vazio
          icone={<FileText className="size-8" />}
          titulo={
            cotacoes.length === 0 ? 'Nenhuma cotação registrada' : 'Nenhuma cotação neste filtro'
          }
          descricao={
            cotacoes.length === 0
              ? 'Registre o que o fornecedor respondeu — preço, quantidade mínima, frete e prazo. É desse registro que sai o comparativo e, depois, a análise de viabilidade.'
              : 'Troque o status ou limpe a busca.'
          }
          acao={
            cotacoes.length === 0 &&
            podeEscrever && (
              <Link
                to="/cotacoes/nova"
                className="rounded bg-marca px-3 py-1.5 text-sm font-semibold text-marca-tinta hover:opacity-90"
              >
                Registrar a primeira
              </Link>
            )
          }
        />
      ) : (
        <>
          <div className="hidden overflow-x-auto rounded-md border border-regua-2 sm:block">
            <table className="w-full min-w-[46rem] border-collapse bg-superficie text-sm">
              <thead>
                <tr>
                  {['Referência', 'Fornecedor', 'Status', 'Validade', 'Enviada', 'Observações'].map(
                    (h) => (
                      <th
                        key={h}
                        className="border-b border-regua-2 px-3 py-2 text-left font-mono text-[0.62rem] font-semibold tracking-wider text-marca uppercase"
                      >
                        {h}
                      </th>
                    ),
                  )}
                </tr>
              </thead>
              <tbody>
                {filtradas.map((c) => (
                  <tr key={c.id} className="border-b border-regua last:border-0 even:bg-marca/4">
                    <td className="px-3 py-2">
                      <Link to={`/cotacoes/${c.id}`} className="num font-medium hover:text-marca">
                        {c.referencia}
                      </Link>
                    </td>
                    <td className="px-3 py-2">
                      {nomeFornecedor[c.fornecedorId] ? (
                        <Link
                          to={`/fornecedores/${c.fornecedorId}`}
                          className="text-tinta-2 hover:text-marca"
                        >
                          {nomeFornecedor[c.fornecedorId]}
                        </Link>
                      ) : (
                        <span className="text-tinta-3 italic">fornecedor removido</span>
                      )}
                    </td>
                    <td className="px-3 py-2">
                      <SeloCotacao status={c.statusEfetivo} />
                    </td>
                    <td className="px-3 py-2">
                      <DataBR iso={c.validade} />
                    </td>
                    <td className="px-3 py-2">
                      <DataBR iso={c.enviadaEm ? isoDeTimestamp(c.enviadaEm) : null} />
                    </td>
                    <td className="max-w-64 truncate px-3 py-2 text-tinta-2">
                      <Texto valor={c.observacoes} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <ul className="flex flex-col gap-2 sm:hidden">
            {filtradas.map((c) => (
              <li key={c.id} className="rounded-md border border-regua-2 bg-superficie p-3">
                <Link to={`/cotacoes/${c.id}`} className="flex flex-col gap-1.5">
                  <span className="flex items-center justify-between gap-2">
                    <span className="num font-medium">{c.referencia}</span>
                    <SeloCotacao status={c.statusEfetivo} />
                  </span>
                  <span className="text-xs text-tinta-2">
                    {nomeFornecedor[c.fornecedorId] ?? 'fornecedor removido'}
                  </span>
                  <span className="text-xs text-tinta-3">
                    validade <DataBR iso={c.validade} />
                  </span>
                </Link>
              </li>
            ))}
          </ul>

          <p className="text-xs text-tinta-3">
            <span className="num">{filtradas.length}</span> de{' '}
            <span className="num">{cotacoes.length}</span>{' '}
            {cotacoes.length === 1 ? 'cotação' : 'cotações'}
          </p>
        </>
      )}
    </div>
  );
}

/** O Firestore devolve Timestamp; a lista mostra só o dia. */
function isoDeTimestamp(t: { toDate: () => Date }): string | null {
  try {
    return t.toDate().toISOString().slice(0, 10);
  } catch {
    return null;
  }
}
